import type { ArchiveType, PeopleImportConfig, PeopleImportPreview, PeopleImportState } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { dateTime, humanize } from '../../lib/format';
import { navigate } from '../../lib/router';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, ErrorText, Select } from '../../ui';

const EMPTY: PeopleImportConfig = { tipo: '', nome: '', cognome: null, email: null, telefono: null, ruoli: [], reparto: null, responsabile: null, attivo: null, disattiva_assenti: true };

function Summary({ r }: { r: PeopleImportPreview }) {
  return (
    <div className="space-y-2 text-sm">
      <p>
        {r.righe} persone nei dati: <b>{r.nuove}</b> nuove, <b>{r.aggiornate}</b> da aggiornare, <b>{r.disattivate}</b> da disattivare.
        {r.ruoli_nuovi.length > 0 && ` Ruoli nuovi: ${r.ruoli_nuovi.slice(0, 12).join(', ')}${r.ruoli_nuovi.length > 12 ? '…' : ''}.`}
        {r.responsabili_non_trovati > 0 && ` ${r.responsabili_non_trovati} responsabili non riconosciuti.`}
      </p>
      {r.avvisi.map((a) => (
        <p key={a} className="text-xs text-amber-800">
          {a}
        </p>
      ))}
      {r.campione.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-ink/55">
              <tr>
                <th className="py-1 pr-3 font-medium">Persona</th>
                <th className="pr-3 font-medium">Ruoli</th>
                <th className="pr-3 font-medium">Reparto</th>
                <th className="pr-3 font-medium">Responsabile</th>
                <th className="font-medium" />
              </tr>
            </thead>
            <tbody>
              {r.campione.map((c, i) => (
                <tr key={i} className="border-t border-black/5">
                  <td className="py-1 pr-3">
                    {c.nome}
                    {c.email && <span className="block text-ink/45">{c.email}</span>}
                  </td>
                  <td className="pr-3">{c.ruoli.join(', ') || '—'}</td>
                  <td className="pr-3">{c.reparto ?? '—'}</td>
                  <td className="pr-3">{c.responsabile ?? '—'}</td>
                  <td>{c.esito !== 'invariata' && <Badge tone={c.esito === 'nuova' ? 'green' : 'amber'}>{c.esito}</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Persone e ruoli dall'archivio: l'organigramma arriva da un programma collegato o da un export. Il
 * sistema propone l'abbinamento, mostra l'anteprima e, confermato, lo tiene allineato a ogni sincronizzazione.
 */
export function PeopleImport({ onClose }: { onClose: () => void }) {
  const { tenant, t } = useTenant();
  const queryClient = useQueryClient();
  const state = useTenantQuery<PeopleImportState>(['people-import'], '/people/import');
  const types = useTenantQuery<{ data: ArchiveType[] }>(['archive', 'types'], '/archive/types');
  const [config, setConfig] = useState<PeopleImportConfig>(EMPTY);
  const [preview, setPreview] = useState<PeopleImportPreview | null>(null);
  const [done, setDone] = useState<PeopleImportPreview | null>(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!state.data || config.tipo) return;
    const initial = state.data.config ?? state.data.proposte[0]?.config;
    if (initial) setConfig(initial);
  }, [state.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id] });
  const test = useMutation({ mutationFn: (c: PeopleImportConfig) => api<PeopleImportPreview>(t('/people/import/preview'), { body: c }), onSuccess: setPreview });
  const apply = useMutation({
    mutationFn: () => api<PeopleImportPreview>(t('/people/import'), { body: config }),
    onSuccess: (r) => {
      setDone(r);
      setPreview(null);
      setEditing(false);
      refresh();
    },
  });
  const rerun = useMutation({ mutationFn: () => api<PeopleImportPreview>(t('/people/import/run'), { method: 'POST' }), onSuccess: (r) => (setDone(r), refresh()) });
  const stop = useMutation({ mutationFn: () => api(t('/people/import/remove'), { method: 'POST' }), onSuccess: refresh });

  const ty = types.data?.data.find((x) => x.nome === config.tipo);
  const attrs = ty?.attributi ?? [];
  const pick = (label: string, field: 'nome' | 'cognome' | 'email' | 'telefono' | 'reparto' | 'responsabile' | 'attivo', required = false) => (
    <Select label={label} value={config[field] ?? ''} onChange={(e) => (setConfig({ ...config, [field]: e.target.value || null }), setPreview(null))} required={required}>
      <option value="">{required ? '' : '— nessuno —'}</option>
      {attrs.map((a) => (
        <option key={a.nome} value={a.nome}>
          {humanize(a.nome)}
        </option>
      ))}
    </Select>
  );
  const saved = state.data?.config;
  const showForm = !saved || editing;

  return (
    <Card>
      <div className="mb-3 flex items-start gap-2">
        <div className="flex-1">
          <h2 className="text-sm font-semibold">Importa persone e ruoli</h2>
          <p className="text-xs text-ink/55">Dall’organigramma che avete già: un programma collegato o un export caricato in Archivio. Dopo la conferma resta allineato da solo.</p>
        </div>
        <button className="text-xs text-ink/50 hover:text-ink" onClick={onClose}>
          chiudi
        </button>
      </div>

      {saved && !editing && (
        <div className="space-y-2 text-sm">
          <p>
            Le persone arrivano da <b>{types.data?.data.find((x) => x.nome === saved.tipo)?.etichetta ?? humanize(saved.tipo)}</b>
            {saved.ruoli.length > 0 && <> · ruoli da {saved.ruoli.map(humanize).join(', ')}</>}
            {saved.reparto && <> · reparto da {humanize(saved.reparto)}</>}. Ultimo allineamento: {dateTime(state.data!.last_run_at)}.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" disabled={rerun.isPending} onClick={() => rerun.mutate()}>
              {rerun.isPending ? 'Allineamento…' : 'Allinea ora'}
            </Button>
            <Button variant="ghost" onClick={() => setEditing(true)}>
              Cambia abbinamento
            </Button>
            <Button variant="ghost" onClick={() => confirm('Smettere di allineare le persone? Quelle già importate restano.') && stop.mutate()}>
              Smetti di allineare
            </Button>
          </div>
        </div>
      )}

      {showForm && state.data && types.data && (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            test.mutate(config);
          }}
        >
          {types.data.data.length === 0 ? (
            <div className="space-y-2 text-sm text-ink/65">
              <p>Non ci sono ancora dati da cui importare. Due strade:</p>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="ghost" onClick={() => navigate(`/t/${tenant.id}/software`)}>
                  Collega il programma delle persone
                </Button>
                <Button type="button" variant="ghost" onClick={() => navigate(`/t/${tenant.id}/archivio`)}>
                  Carica un export (Excel/CSV)
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Select
                label="Da quali dati"
                value={config.tipo}
                onChange={(e) => {
                  const proposal = state.data!.proposte.find((p) => p.tipo === e.target.value);
                  setConfig(proposal?.config ?? { ...EMPTY, tipo: e.target.value });
                  setPreview(null);
                }}
                required
              >
                <option value="" />
                {types.data.data.map((x) => (
                  <option key={x.nome} value={x.nome}>
                    {x.etichetta} ({x.entita}){state.data!.proposte.some((p) => p.tipo === x.nome) ? ' · sembra un elenco di persone' : ''}
                  </option>
                ))}
              </Select>
              {ty && (
                <>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {pick('Nome (o nome e cognome)', 'nome', true)}
                    {pick('Cognome, se separato', 'cognome')}
                    {pick('Email', 'email')}
                    {pick('Telefono', 'telefono')}
                    {pick('Reparto', 'reparto')}
                    {pick('Responsabile', 'responsabile')}
                    {pick('Attivo (se c’è)', 'attivo')}
                  </div>
                  <div>
                    <p className="mb-1 text-sm font-medium text-ink/80">Cosa diventa ruolo</p>
                    <div className="flex flex-wrap gap-1">
                      {attrs.map((a) => (
                        <button
                          type="button"
                          key={a.nome}
                          className={`rounded-full border px-2 py-0.5 text-xs ${config.ruoli.includes(a.nome) ? 'border-accent bg-accent/10' : 'border-black/15'}`}
                          onClick={() => {
                            setConfig({ ...config, ruoli: config.ruoli.includes(a.nome) ? config.ruoli.filter((r) => r !== a.nome) : [...config.ruoli, a.nome].slice(0, 3) });
                            setPreview(null);
                          }}
                        >
                          {humanize(a.nome)}
                        </button>
                      ))}
                    </div>
                    <p className="mt-1 text-xs text-ink/45">Es. mansione o reparto: ogni valore diverso diventa un ruolo per l’assegnazione dei compiti.</p>
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={config.disattiva_assenti} onChange={(e) => setConfig({ ...config, disattiva_assenti: e.target.checked })} />
                    Disattiva chi non compare più nei dati (non viene cancellato)
                  </label>
                </>
              )}
              <div className="flex flex-wrap gap-2">
                <Button type="submit" variant="ghost" disabled={!config.tipo || !config.nome || test.isPending}>
                  {test.isPending ? 'Lettura…' : 'Mostra anteprima'}
                </Button>
                {preview && (
                  <Button type="button" disabled={apply.isPending} onClick={() => apply.mutate()}>
                    {apply.isPending ? 'Importazione…' : 'Conferma e importa'}
                  </Button>
                )}
                {editing && (
                  <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
                    Annulla
                  </Button>
                )}
              </div>
            </>
          )}
        </form>
      )}

      {preview && (
        <div className="mt-4 rounded-lg bg-black/[0.03] p-3">
          <Summary r={preview} />
        </div>
      )}
      {done && (
        <div className="mt-4 rounded-lg bg-emerald-50 p-3">
          <p className="mb-1 text-sm font-medium text-emerald-800">Fatto.</p>
          <Summary r={done} />
        </div>
      )}
      <ErrorText>{test.error ? errorMessage(test.error) : apply.error ? errorMessage(apply.error) : rerun.error ? errorMessage(rerun.error) : state.error ? errorMessage(state.error) : ''}</ErrorText>
    </Card>
  );
}
